import { shouldHighlightEmptyTomorrowCell } from "./highlightEmptyTomorrowCell";
import { datesEqual } from "./dateCompare";
import { OFF_ATTRIBUTE_VALUE } from "./installerHasFullWeekOff";

// - - - - - - - - - -
// #139 Phase 7 — Next Day Openings Dashboard aggregation helper.
//
// Given the raw Lambda responses fetched by useDashboardOpenings (one set
// per location), produce a per-location list of installers whose tomorrow
// cell would qualify for the same red highlight #138 already shows on the
// Optimizer grid.
//
// CRITICAL: the eligibility decision DELEGATES to
// shouldHighlightEmptyTomorrowCell (#138). The dashboard must agree with
// what the Optimizer's grid shows; both call the SAME helper to guarantee
// parity. The only work here is shaping raw Lambda responses into the
// scheduleArray-doc shape that shouldHighlightEmptyTomorrowCell expects.
//
// This module purposefully does NOT pull from ScheduleContext — it works
// on raw Lambda payloads so it stays pure + unit-testable. The trade-off
// is replicating two pieces of logic that exist in schedule.context.js:
//   1. Compliance-hold doc construction (schedule.context.js:1617-1647)
//   2. Default-availability doc materialization (schedule.context.js
//      :1355-1505, the weekDatesArray.forEach loop)
// Both are simple enough that drift risk is low and the tests in this
// module pin the behavior.
//
// The payrollNo formatting mirrors AppContext's `dataFormatting_payrollFields`
// — zero-pad to 7 chars total, with an extra trailing zero when the
// suffix is one digit (e.g. "3264.4" → "3264.40"). Extracted as a pure
// function so test fixtures can produce matching shapes without spinning
// up a context provider.

// - - - - - - - - - - shape helpers

/**
 * Mirror of `dataFormatting_payrollFields` in app.context.js. Pure.
 * Returns the canonical string form used everywhere in the app to compare
 * installer payrollNos.
 */
export function payrollFormat(raw) {
  if (raw === null || raw === undefined || raw === "") return "";
  const str = String(raw);
  const length = str.length;
  const decimalPos = str.indexOf(".");
  let normalized = str;
  if (decimalPos === length - 2) {
    // suffix has a single digit, append a "0" → "3264.4" → "3264.40"
    normalized = str + "0";
  }
  return normalized.padStart(7, "0");
}

/**
 * Extract the projects array from a getProjectsByCompany Lambda response.
 * Returns [] for missing / unknown shapes so callers never crash.
 */
export function extractProjects(rawPayload) {
  if (!rawPayload) return [];
  const result = rawPayload?.message?.result;
  if (Array.isArray(result?.projects)) return result.projects;
  if (Array.isArray(result)) return result;
  return [];
}

/**
 * Extract the candidates array from a getCandidatesByCompany Lambda response.
 * Handles both the new object format (`{ candidates: [...] }`) and the legacy
 * array-direct format.
 */
export function extractCandidates(rawPayload) {
  if (!rawPayload) return [];
  const result = rawPayload?.message?.result;
  if (Array.isArray(result?.candidates)) return result.candidates;
  if (Array.isArray(result)) return result;
  return [];
}

/**
 * Extract the attributes array from a getAttributesByLocation Lambda response.
 */
export function extractAttributes(rawPayload) {
  if (!rawPayload) return [];
  const result = rawPayload?.message?.result;
  if (Array.isArray(result)) return result;
  if (Array.isArray(result?.attributes)) return result.attributes;
  return [];
}

// - - - - - - - - - - scheduleArray construction

// Field-name keys used inside default-availability sub-objects. The "Thr"
// (not "Thu") is intentional — mirrors what schedule.context.js looks for
// when materializing defaults into per-weekday OFF docs (see lines
// 1382-1505 there).
const WEEKDAY_KEYS_BY_DAY_INDEX = [
  "Sun",
  "Mon",
  "Tue",
  "Wed",
  "Thr",
  "Fri",
  "Sat",
];

/**
 * Construct the minimum scheduleArray-shaped doc set needed by
 * shouldHighlightEmptyTomorrowCell for evaluating ONE date (tomorrow)
 * at ONE location.
 *
 * The doc shape mirrors what schedule.context.js's createScheduleArray
 * produces — job-tile docs (attribute !== true) and attribute docs
 * (attribute === true). We only construct docs covering tomorrow's date
 * for installers at this location; everything else is irrelevant.
 */
export function buildTomorrowScheduleArray({
  locationCode,
  candidates,
  attributes,
  projects,
  tomorrowDateString,
}) {
  if (!tomorrowDateString || !locationCode) return [];
  const tomorrowMs = new Date(tomorrowDateString).getTime();
  if (Number.isNaN(tomorrowMs)) return [];

  const tomorrowDayIndex = new Date(tomorrowDateString).getDay();
  const tomorrowWeekdayKey = WEEKDAY_KEYS_BY_DAY_INDEX[tomorrowDayIndex];

  const scheduleArray = [];

  // 1. Job-tile docs — projects where (statusCode !== 20 cancelled) AND
  //    date range [date_scheduled..date_estimatedCompletion] covers
  //    tomorrow. Per #138, the location filter is intentionally dropped
  //    here so shared-installer cross-location assignments still count
  //    as booked.
  //
  //    #161 (2026-05-26, Brandon): originally this filter required
  //    `statusCode === 4` strict. That missed every post-Scheduled
  //    in-progress / billing status (5 Ready to Dispatch, 6 In Transit,
  //    7 Checked In, 8 Checked Out, 9 COC Received, 10–13, 14 Billed,
  //    14.1, plus 30/30.1 Repair Open). The Optimizer's cell render
  //    has no statusCode filter — it shows ANY tile with non-empty
  //    titles. The dashboard MUST match because Brandon was correctly
  //    seeing in-progress (sc=7) multi-day jobs on tomorrow's cell in
  //    the Optimizer while the dashboard still listed those installers
  //    as openings. Confirmed via PROD Time Machine forensic for
  //    installers 0827.01 (BHM, 3 sc=7 jobs) and 1990.01 (ATL, 1 sc=7
  //    AUG cross-location job). Fix: exclude only statusCode 20
  //    (cancelled), matching the production filteredProjectsList rule
  //    in schedule.context.js.
  for (const p of Array.isArray(projects) ? projects : []) {
    if (!p) continue;
    const status = parseFloat(p.statusCode);
    if (status === 20) continue;
    if (!p.installer && p.installer !== 0) continue;
    if (!p.date_scheduled) continue;
    const startMs = new Date(p.date_scheduled).getTime();
    if (Number.isNaN(startMs)) continue;
    const endRaw = p.date_estimatedCompletion || p.date_scheduled;
    const endMs = new Date(endRaw).getTime();
    if (Number.isNaN(endMs)) continue;
    if (tomorrowMs < startMs || tomorrowMs > endMs) continue;
    scheduleArray.push({
      installer: payrollFormat(p.installer),
      // location intentionally omitted — shouldHighlightEmptyTomorrowCell's
      // job check doesn't filter on location (matches schedule.context.js
      // line 1893 comment). Including it would be inert but misleading.
      start: p.date_scheduled,
      end: endRaw,
    });
  }

  // 2. Attribute docs — daily AND default-pattern materialization.
  for (const a of Array.isArray(attributes) ? attributes : []) {
    if (!a) continue;
    if (!a._kf_payrollNo && a._kf_payrollNo !== 0) continue;
    const installer = payrollFormat(a._kf_payrollNo);
    const attrLocation = a.location;

    // 2a. Daily attribute on tomorrow's date.
    if (a.date && new Date(a.date).getTime() === tomorrowMs) {
      scheduleArray.push({
        attribute: true,
        installer,
        location: attrLocation,
        start: tomorrowDateString,
        encodedSmallTitle: a.encodedSmallTitle ?? "",
        encodedLargeTitle: a.encodedLargeTitle ?? "",
      });
      continue;
    }

    // 2b. Default-pattern attribute (date === "" and an availability array).
    //     Materialize into a per-weekday doc for tomorrow's weekday if the
    //     default specifies one. Mirrors schedule.context.js:1382-1505.
    if (
      (a.date === "" || a.date === null || a.date === undefined) &&
      Array.isArray(a.availability)
    ) {
      const dayEntry = a.availability.find((d) => d && d[tomorrowWeekdayKey]);
      if (dayEntry) {
        const status = dayEntry[tomorrowWeekdayKey];
        scheduleArray.push({
          attribute: true,
          installer,
          location: attrLocation,
          start: tomorrowDateString,
          encodedSmallTitle: status,
          encodedLargeTitle: status,
        });
      }
    }
  }

  // 3. Compliance-hold docs — derived from candidate's
  //    Scheduling_Hold_Effective_Date. Mirrors schedule.context.js:1617-1647.
  for (const c of Array.isArray(candidates) ? candidates : []) {
    if (!c) continue;
    if (!c.Scheduling_Hold_Effective_Date) continue;
    const effectiveMs = new Date(c.Scheduling_Hold_Effective_Date).getTime();
    if (Number.isNaN(effectiveMs)) continue;
    if (tomorrowMs < effectiveMs) continue;
    scheduleArray.push({
      attribute: true,
      complianceHold: true,
      installer: payrollFormat(c.payrollNo),
      location: c.cLocationCode,
      start: tomorrowDateString,
      encodedSmallTitle: "🔥 compliance hold 🔥",
      encodedLargeTitle: "🔥 compliance hold 🔥",
      locked: true,
    });
  }

  return scheduleArray;
}

// - - - - - - - - - - per-location aggregation

/**
 * For an opening installer (cell qualified for the red highlight), return a
 * display-friendly comma-joined string of any attribute markers present on
 * the cell that DIDN'T suppress the highlight — e.g. "BUFFER", "TRAINING",
 * default-pattern non-OFF values, or anything custom the scheduler typed.
 *
 * Returns "" when the cell is truly blank (no attribute doc for this
 * installer on tomorrow). The caller decides how to render the blank case
 * (em-dash, blank, etc.).
 *
 * NOTE: OFF and compliance-hold docs are EXCLUDED here because those
 * suppress the opening entirely — they cannot appear next to an opening
 * row. Filtering them out is defense-in-depth in case the upstream
 * `shouldHighlightEmptyTomorrowCell` semantics ever change.
 */
function getCellContentForInstaller({
  scheduleArray,
  payrollNo,
  locationFilter,
  tomorrowDateString,
}) {
  if (!Array.isArray(scheduleArray)) return "";
  const titles = [];
  for (const doc of scheduleArray) {
    if (!doc) continue;
    if (doc.attribute !== true) continue;
    if (doc.installer !== payrollNo) continue;
    if (doc.location !== locationFilter) continue;
    if (!datesEqual(doc.start, tomorrowDateString)) continue;
    if (doc.complianceHold === true) continue;
    if (doc.encodedSmallTitle === OFF_ATTRIBUTE_VALUE) continue;
    const title = doc.encodedSmallTitle;
    if (title === undefined || title === null || title === "") continue;
    titles.push(title);
  }
  return titles.join(", ");
}

// Replicates the two-step sort applied to the Optimizer's
// `filteredCandidatesList` (see modelTS.context.js `getCandidatesByCompany`
// select callback ~line 2247): primary `payrollNo` ascending as a number,
// secondary `Scheduling_Surface` descending as text. The descending text
// sort puts "Soft Surface" before "Hard Surface" — same upper-half /
// lower-half visual grouping as the grid's installer rows.
//
// Source of truth: modelTS.context.js's myJsonArraySort calls. If that
// canonical order ever changes, update this mirror in lockstep.
function sortCandidatesGridOrder(candidates) {
  // Don't mutate the caller's array. JS .sort is in-place; copy first.
  return candidates.slice().sort((a, b) => {
    // Primary: payrollNo ASC as numbers
    const numA = Number(a?.payrollNo);
    const numB = Number(b?.payrollNo);
    const aNaN = Number.isNaN(numA);
    const bNaN = Number.isNaN(numB);
    if (aNaN && bNaN) {
      // both unparseable — fall through to secondary
    } else if (aNaN) {
      return 1; // NaN sorts after numbers
    } else if (bNaN) {
      return -1;
    } else if (numA !== numB) {
      return numA - numB;
    }
    // Secondary: Scheduling_Surface DESC as text (case-aware via localeCompare)
    const surfA = String(a?.Scheduling_Surface ?? "");
    const surfB = String(b?.Scheduling_Surface ?? "");
    return surfB.localeCompare(surfA, undefined, { caseFirst: "upper" });
  });
}

// Surface-filter constants. Persisted as Users.dashboardSurfaceFilter in
// MongoDB (default "both" when the field is missing — no seed migration
// needed). Exported so the modal + payload builder can validate against
// the same source of truth.
export const DASHBOARD_SURFACE_FILTER_VALUES = Object.freeze([
  "soft",
  "hard",
  "both",
]);
export const DASHBOARD_SURFACE_FILTER_DEFAULT = "both";

/**
 * Filter a candidates list by Scheduling_Surface value. Pure.
 *
 *   "soft" → only candidates with Scheduling_Surface === "Soft Surface"
 *   "hard" → only candidates with Scheduling_Surface === "Hard Surface"
 *   "both" → no filter (input returned as-is, no copy)
 *   any other value → falls back to "both" (no filter)
 *
 * Returns the SAME array reference when no filter applies so downstream
 * memoization / identity checks remain stable.
 */
export function filterCandidatesBySurface(candidates, surfaceFilter) {
  if (!Array.isArray(candidates)) return [];
  if (surfaceFilter === "soft") {
    return candidates.filter((c) => c?.Scheduling_Surface === "Soft Surface");
  }
  if (surfaceFilter === "hard") {
    return candidates.filter((c) => c?.Scheduling_Surface === "Hard Surface");
  }
  return candidates;
}

/**
 * Returns the list of installers at this location whose tomorrow cell
 * would qualify for #138's red highlight (i.e., genuinely-empty openings).
 * Sorted to match the Optimizer grid's installer-row order.
 *
 * Each installer object:
 *   {
 *     payrollNo:         formatted, matches the grid's display payroll string
 *     name:              c.Name_Full (canonical, matches the grid's renderCrewCell)
 *                        with fallback chain for legacy / projection variants
 *     homeLocation:      c.cLocationCode or this location
 *     schedulingSurface: e.g. "Soft Surface" / "Hard Surface"
 *     cellContent:       "" if truly blank, else comma-joined attribute titles
 *                        (e.g. "BUFFER", "TRAINING") present on the cell that
 *                        didn't suppress the opening
 *   }
 *
 * `surfaceFilter` (optional, default "both") restricts the candidate set
 * to one half of the grid. The scheduleArray is STILL built from every
 * candidate at this location, so cross-installer attributes / projects
 * are present for context — but only filtered candidates are evaluated
 * for openings and appear in the result.
 */
export function buildLocationOpenings({
  locationCode,
  candidates,
  attributes,
  projects,
  tomorrowDateString,
  surfaceFilter = DASHBOARD_SURFACE_FILTER_DEFAULT,
}) {
  if (!tomorrowDateString || !locationCode) return [];
  if (!Array.isArray(candidates)) return [];

  const scheduleArray = buildTomorrowScheduleArray({
    locationCode,
    candidates,
    attributes,
    projects,
    tomorrowDateString,
  });

  const filteredCandidates = filterCandidatesBySurface(
    candidates,
    surfaceFilter
  );

  const filtered = filteredCandidates.filter((c) => {
    if (!c) return false;
    const payroll = payrollFormat(c.payrollNo);
    if (!payroll) return false;
    return shouldHighlightEmptyTomorrowCell({
      dateString: tomorrowDateString,
      nextBusinessDayDateString: tomorrowDateString,
      scheduleArray,
      payrollNo: payroll,
      locationFilter: locationCode,
    });
  });

  const sorted = sortCandidatesGridOrder(filtered);

  return sorted.map((c) => {
    const payroll = payrollFormat(c.payrollNo);
    return {
      payrollNo: payroll,
      name:
        c.Name_Full ||
        c.Calc_Name_Full ||
        c.schedulingName ||
        c.Calc_Installer_Payroll_Name ||
        "(unknown)",
      homeLocation: c.cLocationCode || locationCode,
      schedulingSurface: c.Scheduling_Surface || "",
      cellContent: getCellContentForInstaller({
        scheduleArray,
        payrollNo: payroll,
        locationFilter: locationCode,
        tomorrowDateString,
      }),
    };
  });
}

// - - - - - - - - - - top-level dashboard build

/**
 * Build the dashboard's full per-location openings structure.
 *
 * @param {Object} params
 * @param {Object} params.dataByLocation       — output of useDashboardOpenings
 * @param {string} params.tomorrowDateString   — next-business-day en-us locale string
 * @param {string} [params.surfaceFilter]      — "soft" | "hard" | "both" (default "both")
 * @returns {{
 *   locations: Array<{
 *     locationCode: string,
 *     openings: Array<{ payrollNo, name, homeLocation, schedulingSurface, cellContent }>,
 *     isLoading: boolean,
 *     isError: boolean,
 *     candidatesCount: number,   // reflects surface filter
 *   }>,
 *   totalOpenings: number,
 *   totalCandidates: number,     // reflects surface filter
 * }}
 *
 * `candidatesCount` and `totalCandidates` reflect the SURFACE-FILTERED
 * candidate set — so when the user picks "Soft" only, "/ 18 installers"
 * displays the soft-installer count at that location, not the raw 29.
 */
export function buildDashboardOpenings({
  dataByLocation,
  tomorrowDateString,
  surfaceFilter = DASHBOARD_SURFACE_FILTER_DEFAULT,
}) {
  const locations = [];
  let totalOpenings = 0;
  let totalCandidates = 0;

  for (const locationCode of Object.keys(dataByLocation || {}).sort()) {
    const entry = dataByLocation[locationCode] || {};
    const candidates = extractCandidates(entry.candidates);
    const attributes = extractAttributes(entry.attributes);
    const projects = extractProjects(entry.projects);

    const filteredCandidates = filterCandidatesBySurface(
      candidates,
      surfaceFilter
    );

    const openings = entry.isLoading
      ? []
      : buildLocationOpenings({
          locationCode,
          candidates,
          attributes,
          projects,
          tomorrowDateString,
          surfaceFilter,
        });

    // Per-surface breakdown so the location header line can split counts
    // when the user has "both" surfaces selected. Counts are computed over
    // the post-filter set: when filter is "soft", hardCount fields are 0
    // (and vice versa); when filter is "both" both are populated naturally.
    const softCandidatesCount = filteredCandidates.filter(
      (c) => c?.Scheduling_Surface === "Soft Surface"
    ).length;
    const hardCandidatesCount = filteredCandidates.filter(
      (c) => c?.Scheduling_Surface === "Hard Surface"
    ).length;
    const softOpeningsCount = openings.filter(
      (o) => o.schedulingSurface === "Soft Surface"
    ).length;
    const hardOpeningsCount = openings.filter(
      (o) => o.schedulingSurface === "Hard Surface"
    ).length;

    locations.push({
      locationCode,
      openings,
      isLoading: !!entry.isLoading,
      // isFetching propagated through so the LocationGroup status
      // badge can show "refreshing…" on per-location rows during a
      // user-initiated refresh or a close-reopen refetch. Without it,
      // only the modal-level status badge would update and individual
      // rows would silently show stale numbers + a green "ready"
      // appearance during the refresh.
      isFetching: !!entry.isFetching,
      isError: !!entry.isError,
      candidatesCount: filteredCandidates.length,
      softCandidatesCount,
      hardCandidatesCount,
      softOpeningsCount,
      hardOpeningsCount,
    });

    totalOpenings += openings.length;
    totalCandidates += filteredCandidates.length;
  }

  return { locations, totalOpenings, totalCandidates };
}
